"use client";

import type { ReportCatalog, ReportPeriod } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import React, { useState } from "react";
import {
  floorsByOffice,
  floorsOfOffices,
  type OfficeFloor
} from "../lib/boards";
import { ReportEmptyState } from "./ReportEmptyState";
import { ReportMapView } from "./ReportMapView";

interface OfficeMapCardProps {
  catalog: ReportCatalog;
  measure: string;
  /** The offices whose floors are on offer; several get a row of tabs each. */
  officeIds: string[];
  period: ReportPeriod;
  height?: number;
  initialMode?: "period" | "assignments";
}

interface FloorTabsProps {
  floors: OfficeFloor[];
  pickedId: string;
  onPick: (id: string) => void;
}

const FloorTabs: React.FC<FloorTabsProps> = ({ floors, pickedId, onPick }) => (
  <div className="flex flex-wrap items-center gap-1">
    {floors.map((f) => (
      <button
        type="button"
        key={f.id}
        onClick={() => onPick(f.id)}
        className={cn(
          "h-7 rounded-full px-3 text-[12.5px]",
          f.id === pickedId
            ? "bg-neutral-900 font-medium text-white"
            : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
        )}
      >
        {f.label}
      </button>
    ))}
  </div>
);

/**
 * An office's floors, one plan at a time: a row of floor tabs and the map
 * of the picked one, starting with the first. Several offices get a row of
 * tabs each, under the office's name.
 */
export const OfficeMapCard: React.FC<OfficeMapCardProps> = ({
  catalog,
  measure,
  officeIds,
  period,
  height,
  initialMode
}) => {
  const floors = floorsOfOffices(catalog, officeIds);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const floor = floors.find((f) => f.id === pickedId) ?? floors[0];

  if (!floor) {
    return (
      <ReportEmptyState
        title="No floor plans in this office"
        hint="Floors with a plan show up here once the office has them."
        className="py-6"
      />
    );
  }
  const groups = floorsByOffice(floors);

  return (
    <div className="flex flex-col gap-2">
      {groups.length === 1 ? (
        <FloorTabs
          floors={groups[0].floors}
          pickedId={floor.id}
          onPick={setPickedId}
        />
      ) : (
        <div className="flex flex-col gap-1.5">
          {groups.map((group) => (
            <div key={group.office} className="flex items-center gap-2">
              <span className="w-[140px] shrink-0 truncate text-[11px] text-neutral-500 uppercase tracking-wider">
                {group.office}
              </span>
              <FloorTabs
                floors={group.floors}
                pickedId={floor.id}
                onPick={setPickedId}
              />
            </div>
          ))}
        </div>
      )}
      <ReportMapView
        key={floor.id}
        catalog={catalog}
        measure={measure}
        floorId={floor.id}
        period={period}
        height={height}
        initialMode={initialMode}
      />
    </div>
  );
};
